import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Nav, Spinner, Tab } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listEcoDictionaryItems,
  resolveEcoDictionaryItem,
  selectEcoById,
  updateEcoById,
  upsertEcoDictionaryItem,
} from 'services/ecodoppler';
import 'styles/laudo-base.css';
import CampoMedida from 'components/laudos/CampoMedida';
import ListaDicionario from 'components/laudos/ListaDicionario';
import ListaDicionarioComposta from 'components/laudos/ListaDicionarioComposta';
import useSWR, { mutate } from 'swr';

const topTabList = [
  { key: 'congenitos', label: 'Congênitos' },
  { key: 'diagnosticos', label: 'Diagnósticos' },
  { key: 'valv-mitral', label: 'Valv. Mitral' },
  { key: 'valv-aortica', label: 'Valv. Aórtica' },
  { key: 'valv-tri-pulm', label: 'Valv. Tri/Pulm' },
  { key: 'valv-proteses', label: 'Valv. Próteses' },
  { key: 'conclusoes', label: 'Conclusões' },
  { key: 'observacoes', label: 'Observações' },
];

const bottomTabList = [
  { key: 'dados-pessoais', label: 'Dados Pessoais' },
  { key: 'funcao-sistolica', label: 'Função Sistólica do VE' },
  { key: 'ae-vm', label: 'A.E. e V.M.' },
  { key: 'valva-ao-aorta', label: 'Valva AO e Aorta' },
  { key: 'cavidades-direita-valvar', label: 'Cavidades Direita/Valvar' },
  { key: 'pericardio', label: 'Pericárdio' },
  { key: 'obs-congenitas', label: 'Obs Congênitas' },
];

const tabList = [...bottomTabList, ...topTabList];

const tabHeadingMap = {
  'dados-pessoais': 'Dados Pessoais',
  'funcao-sistolica': 'Diâmetros e Função Sistólica do Ventrículo Esquerdo',
  'ae-vm': 'Átrio Esquerdo e Valva Mitral',
  'valva-ao-aorta': 'Valva Aórtica e Aorta',
  'cavidades-direita-valvar': 'Cavidades Direitas e Valvar',
  pericardio: 'Pericárdio',
  'obs-congenitas': 'Observações Congênitas',
  congenitos: 'Avaliação de Cardiopatias Congênitas',
  diagnosticos: 'Diagnósticos - Estruturas Cardíacas Congênitas',
  'valv-mitral': 'Valva Mitral',
  'valv-aortica': 'Valva Aórtica',
  'valv-tri-pulm': 'Válvulas Tricúspide e Pulmonar',
  'valv-proteses': 'Valvulopatia em Próteses',
  conclusoes: 'Conclusões',
  observacoes: 'Observações',
};

const listDictionaryConfig = {
  obs: { group: 'obs', codeKey: 'COD' },
  achados: { group: 'achados', codeKey: 'COD' },
  contraRegiao: {
    group: 'contraSeg',
    codeKey: 'COD',
    textKey: 'CONTRASEG',
    codeLabel: 'Cod',
    textLabel: 'Contração Segmentar',
    extraFields: [
      {
        codeKey: 'CODREGIAO',
        group: 'regiao',
        codeLabel: 'Reg',
        textKey: 'regiaoDescricao',
        textLabel: 'Região',
      },
    ],
  },
  atrioEsq: { group: 'atrioEsq', codeKey: 'COD' },
  atrioDir: { group: 'atrioDir', codeKey: 'COD' },
  aorta: { group: 'aorta', codeKey: 'COD' },
  veiaCavaInf: { group: 'veiaCavaInf', codeKey: 'COD' },
  ventriDir: { group: 'ventriDir', codeKey: 'COD' },
  pericardio: { group: 'pericardio', codeKey: 'COD' },
  situsCardiaco: { group: 'situsCardiaco', codeKey: 'COD' },
  septoInteratrial: { group: 'septoInteratrial', codeKey: 'COD' },
  septoInterventricular: {
    group: 'septoInterventricular',
    codeKey: 'COD',
    textKey: 'DESCRICAO',
    codeLabel: 'Cod',
    textLabel: 'Descrição',
    extraFields: [
      {
        codeKey: 'CODREGIAO',
        group: 'regiao',
        codeLabel: 'Reg',
        textKey: 'regiaoDescricao',
        textLabel: 'Região',
      },
    ],
  },
  concordanciaAtrioVent: { group: 'concordanciaAtrioVent', codeKey: 'COD' },
  viasSaida: { group: 'viasSaida', codeKey: 'COD' },
  concordanciaVentArt: { group: 'concordanciaVentArt', codeKey: 'COD' },
  arteriaPulmonar: { group: 'arteriaPulmonar', codeKey: 'COD' },
  arteriaCoronaria: { group: 'arteriaCoronaria', codeKey: 'COD' },
  arcoAortico: { group: 'arcoAortico', codeKey: 'COD' },
  conexaoVenoAtrial: { group: 'conexaoVenoAtrial', codeKey: 'COD' },
  estruturasAnomas: { group: 'estruturasAnomas', codeKey: 'COD' },
  valvaMitral: { group: 'valvaMitral', codeKey: 'COD' },
  valvaAO: { group: 'valvaAO', codeKey: 'COD' },
  valvaTricu: { group: 'valvaTricu', codeKey: 'COD' },
  valvaPulmo: { group: 'valvaPulmo', codeKey: 'COD' },
  refVM: { group: 'refVM', codeKey: 'COD' },
  refVAO: { group: 'refVAO', codeKey: 'COD' },
  refVT: { group: 'refVT', codeKey: 'COD' },
  refVP: { group: 'refVP', codeKey: 'COD' },
  refluxoPM: {
    group: 'refluxoPM',
    codeKey: 'COD',
    textKey: 'refluxoPM',
    codeLabel: 'Cod',
    textLabel: 'Prótese Mitral',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  refluxoPAO: {
    group: 'refluxoPAO',
    codeKey: 'COD',
    textKey: 'refluxoPAO',
    codeLabel: 'Cod',
    textLabel: 'Prótese Aórtica',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  refluxoPT: {
    group: 'refluxoPT',
    codeKey: 'COD',
    textKey: 'refluxoPT',
    codeLabel: 'Cod',
    textLabel: 'Prótese Tricúspide',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  refluxoPP: {
    group: 'refluxoPP',
    codeKey: 'COD',
    textKey: 'refluxoPP',
    codeLabel: 'Cod',
    textLabel: 'Prótese Pulmonar',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
  valvulopatia: {
    group: 'valvulopatia',
    codeKey: 'COD',
    textKey: 'valvulopatia',
    codeLabel: 'Cod',
    textLabel: 'Valvulopatia',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
      {
        codeKey: 'CODPREDOMI',
        group: 'predominancia',
        codeLabel: 'Pred',
        textKey: 'predominanciaDescricao',
        textLabel: 'Predominância',
      },
    ],
  },
  protese: {
    group: 'protese',
    codeKey: 'COD',
    textKey: 'protese',
    codeLabel: 'Cod',
    textLabel: 'Prótese',
    extraFields: [
      {
        codeKey: 'CDODESCRICAO',
        group: 'descricao',
        codeLabel: 'Desc',
        textKey: 'descricaoDescricao',
        textLabel: 'Descrição',
      },
      {
        codeKey: 'CODPREDOMI',
        group: 'predominancia',
        codeLabel: 'Pred',
        textKey: 'predominanciaDescricao',
        textLabel: 'Predominância',
      },
    ],
  },
  conclusoes: {
    group: 'conclusoes',
    codeKey: 'COD',
    textKey: 'conclusoes',
    codeLabel: 'Cod',
    textLabel: 'Conclusão',
    extraFields: [
      {
        codeKey: 'CODINTENSIDADE',
        group: 'intensidade',
        codeLabel: 'Int',
        textKey: 'intensidadeDescricao',
        textLabel: 'Intensidade',
      },
    ],
  },
};

const compositeListKeys = new Set(
  Object.entries(listDictionaryConfig)
    .filter(([, config]) => Array.isArray(config.extraFields) && config.extraFields.length > 0)
    .map(([key]) => key)
);

const dictionaryGroupLabels = {
  obs: 'Obs',
  achados: 'Achados VE',
  contraSeg: 'Contração Segmentar',
  atrioEsq: 'Átrio Esquerdo',
  atrioDir: 'Átrio Direito',
  aorta: 'Aorta',
  veiaCavaInf: 'Veia Cava Inferior',
  ventriDir: 'Ventrículo Direito',
  pericardio: 'Pericárdio',
  situsCardiaco: 'Situs Cardíaco',
  septoInteratrial: 'Septo Interatrial',
  septoInterventricular: 'Septo Interventricular',
  concordanciaAtrioVent: 'Concordância Atrioventricular',
  viasSaida: 'Vias de Saída',
  concordanciaVentArt: 'Concordância Ventricular Arterial',
  arteriaPulmonar: 'Artéria Pulmonar',
  arteriaCoronaria: 'Artéria Coronária',
  arcoAortico: 'Arco Aórtico',
  conexaoVenoAtrial: 'Conexão Veno Atrial',
  estruturasAnomas: 'Estruturas Anômalas',
  valvaMitral: 'Valva Mitral',
  valvaAO: 'Valva Aórtica',
  valvaTricu: 'Valva Tricúspide',
  valvaPulmo: 'Valva Pulmonar',
  refVM: 'Refluxo Mitral',
  refVAO: 'Refluxo Aórtico',
  refVT: 'Refluxo Tricúspide',
  refVP: 'Refluxo Pulmonar',
  refluxoPM: 'Prótese Mitral',
  refluxoPAO: 'Prótese Aórtica',
  refluxoPT: 'Prótese Tricúspide',
  refluxoPP: 'Prótese Pulmonar',
  valvulopatia: 'Valvulopatias',
  protese: 'Próteses',
  conclusoes: 'Conclusões',
};

const dictionaryTabList = Array.from(
  new Set(Object.values(listDictionaryConfig).map((item) => item.group))
).map((group) => ({
  key: group,
  label: dictionaryGroupLabels[group] || group,
}));

const listKeysOrder = [
  'obs',
  'achados',
  'contraRegiao',
  'atrioEsq',
  'atrioDir',
  'aorta',
  'veiaCavaInf',
  'ventriDir',
  'pericardio',
  'situsCardiaco',
  'septoInteratrial',
  'septoInterventricular',
  'concordanciaAtrioVent',
  'viasSaida',
  'concordanciaVentArt',
  'arteriaPulmonar',
  'arteriaCoronaria',
  'arcoAortico',
  'conexaoVenoAtrial',
  'estruturasAnomas',
  'valvaMitral',
  'valvaAO',
  'valvaTricu',
  'valvaPulmo',
  'refVM',
  'refVAO',
  'refVT',
  'refVP',
  'refluxoPM',
  'refluxoPAO',
  'refluxoPT',
  'refluxoPP',
  'valvulopatia',
  'protese',
  'conclusoes',
];

const listKeyAliases = {
  situsCardiaco: ['situs'],
  septoInteratrial: ['septoIA'],
  septoInterventricular: ['septoIV'],
  concordanciaAtrioVent: ['concordAV'],
  viasSaida: ['ecoVS'],
  concordanciaVentArt: ['concordVA'],
  arteriaPulmonar: ['artP'],
  arteriaCoronaria: ['artC'],
  arcoAortico: ['arcoAO'],
  conexaoVenoAtrial: ['conexao'],
  estruturasAnomas: ['estrutura'],
};

const scalarKeyAliases = {
  VTIVSE: ['VTIVSVE', 'VTIVSVE1', 'VTIVSVE2', 'VTIVSVE3', 'VTIVSVE4'],
  VTIPulmonar: ['VTIP'],
  VSVD: ['DVSVD'],
  gradienteVEVD: ['GVEVD'],
  relacaoPS: ['relacaoPSCong', 'relacaoSPP'],
  fluxoPulmonar: ['flPulmCong'],
  fluxoSistemico: ['flSistCong'],
  VTIMitral: ['VTIMITRAL1', 'VTIMITRAL2'],
  raioConvergenciaIM: ['RCIM'],
  velocidadeAliasing: ['VA'],
  velocidadeMaxRMitral: ['VMAXRM'],
  VTIRegurgMitral: ['VTIRM'],
  areaRefluxoIMColor: ['ARIMCLL'],
  areaAE: ['AAE'],
  fluxoRegurgMitral: ['volumeSM'],
  orificioRM: ['ORIFICIO_VM'],
  FRM: ['fracaoLRM'],
  RELIMAECLL: ['relacaoIMAECLL'],
  VTIVSVEAorta: ['VTIVSVE', 'VTIVSVE1', 'VTIVSVE2', 'VTIVSVE3', 'VTIVSVE4'],
  VTIAortico: ['VTIAO'],
  diametroColorIAortica: ['DIAOCLL'],
  RELIAOCLL: ['relacaoIAOCLL'],
  FRA: ['fracaoRA'],
  areaRefluxoTricuspideColor: ['ARTCLL'],
  velocidadePicoRegurgTricuspide: ['VPRT'],
  areaAtrioDireito: ['AAD'],
  gradientePicoIT: ['GPIT'],
};

const hasScalarValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

const normalizeListItem = (listKey, item) => {
  const nextItem = { ...item };

  if (listKey === 'septoInterventricular' && !nextItem.DESCRICAO) {
    nextItem.DESCRICAO = nextItem.septoIV || '';
  }

  return nextItem;
};

function AutoSaveCloudIndicator({ status, className = '', compact = false }) {
  if (status === 'idle') return null;

  const isSaving = status === 'saving';
  const isSaved = status === 'saved';
  const isError = status === 'error';

  const text = isSaving ? 'Salvando...' : isSaved ? 'Tudo salvo' : 'Falha ao salvar';
  const rootClass = `legacy-cloud-save ${className} ${isSaved ? 'is-saved' : ''} ${
    isError ? 'is-error' : ''
  }`;

  return (
    <span className={rootClass} role="status" aria-live="polite">
      <span className="legacy-cloud-save-icon-wrap" aria-hidden="true">
        <svg
          className="legacy-cloud-save-icon"
          viewBox="0 0 24 24"
          focusable="false"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M19 18a4 4 0 0 0 .6-7.95A6.5 6.5 0 0 0 7.06 8.1 4.75 4.75 0 0 0 7 17.5h12z" />
        </svg>
        {isSaving ? <span className="legacy-cloud-save-spinner" /> : null}
        {isSaved ? (
          <svg
            className="legacy-cloud-save-check"
            viewBox="0 0 14 14"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M5.4 10.4 2.1 7.2l1.1-1.2 2.2 2.2L10.8 3l1.1 1.1z" />
          </svg>
        ) : null}
      </span>
      {!compact ? <span className="legacy-cloud-save-text">{text}</span> : null}
    </span>
  );
}

export default function FormEcocongenito() {
  const history = useHistory();
  const { id } = useParams();
  const {
    data: eco,
    isLoading,
    error,
  } = useSWR(id ? ['ecodoppler', id] : null, () => selectEcoById(id));
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [autoSaveUiState, setAutoSaveUiState] = useState('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('dados-pessoais');
  const [resolvingRows, setResolvingRows] = useState({});
  const dictionaryCacheRef = useRef(new Map());
  const autoSaveStatusTimeoutRef = useRef(null);
  const typingAutoSaveTimeoutRef = useRef(null);
  const [showDictionaryModal, setShowDictionaryModal] = useState(false);
  const [activeDictionaryTab, setActiveDictionaryTab] = useState(dictionaryTabList[0]?.key || '');
  const [dictionaryData, setDictionaryData] = useState({});
  const [dictionaryLoading, setDictionaryLoading] = useState({});
  const [dictionaryDraft, setDictionaryDraft] = useState({});
  const [savingDictionaryKey, setSavingDictionaryKey] = useState('');

  const resolveDictionaryValue = useCallback(async ({ group, code }) => {
    const normalizedCode = String(code || '').trim();
    const normalizedGroup = String(group || '').trim();
    if (!normalizedCode || !normalizedGroup) return null;

    const cacheKey = `${normalizedGroup}|${normalizedCode}`;
    if (dictionaryCacheRef.current.has(cacheKey)) {
      return dictionaryCacheRef.current.get(cacheKey);
    }

    const resolved = await resolveEcoDictionaryItem({
      grupo: normalizedGroup,
      codigo: normalizedCode,
    });

    if (resolved) {
      dictionaryCacheRef.current.set(cacheKey, resolved);
    }

    return resolved;
  }, []);

  const getCompositeColumns = useCallback((listKey, keyName) => {
    const listConfig = listDictionaryConfig[listKey] || {};
    const baseColumn = {
      codeKey: listConfig.codeKey || 'COD',
      codeLabel: listConfig.codeLabel || 'Cod',
      textKey: listConfig.textKey || keyName,
      textLabel: listConfig.textLabel || keyName,
    };

    return [baseColumn, ...(listConfig.extraFields || [])];
  }, []);

  useEffect(() => {
    if (!eco) return;

    const normalized = { ...eco };

    const getListByKey = (key) => {
      if (Array.isArray(eco[key])) return eco[key];

      const aliases = listKeyAliases[key] || [];
      for (const alias of aliases) {
        if (Array.isArray(eco[alias])) {
          return eco[alias];
        }
      }

      return [];
    };

    const getScalarByKey = (key) => {
      if (hasScalarValue(eco[key])) return eco[key];

      const aliases = scalarKeyAliases[key] || [];
      for (const alias of aliases) {
        if (hasScalarValue(eco[alias])) {
          return eco[alias];
        }
      }

      return eco[key];
    };

    listKeysOrder.forEach((key) => {
      normalized[key] = getListByKey(key).map((item) => normalizeListItem(key, item));
    });

    Object.keys(scalarKeyAliases).forEach((key) => {
      normalized[key] = getScalarByKey(key);
    });

    setFormData(normalized);
    setHasUnsavedChanges(false);
  }, [eco]);

  useEffect(() => {
    if (!formData) return;

    let isCancelled = false;

    const hydrateCompositeDescriptions = async () => {
      const updates = [];

      for (const listKey of compositeListKeys) {
        const listConfig = listDictionaryConfig[listKey];
        const items = Array.isArray(formData[listKey]) ? formData[listKey] : [];

        for (let index = 0; index < items.length; index += 1) {
          const item = items[index] || {};
          const columns = getCompositeColumns(listKey, listConfig.textKey || 'DESCRICAO');
          const patch = {};
          let hasPatch = false;

          for (const column of columns) {
            const codeValue = String(item?.[column.codeKey] || '').trim();
            const currentText = String(item?.[column.textKey] || '').trim();

            if (!codeValue || currentText) continue;

            const resolved = await resolveDictionaryValue({
              group:
                column.codeKey === (listConfig.codeKey || 'COD')
                  ? listConfig.group || listKey
                  : column.group,
              code: codeValue,
            });

            if (resolved?.descricao) {
              patch[column.textKey] = resolved.descricao;
              hasPatch = true;
            }
          }

          if (hasPatch) {
            updates.push({ listKey, index, patch });
          }
        }
      }

      if (isCancelled || updates.length === 0) return;

      setFormData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };

        updates.forEach(({ listKey, index, patch }) => {
          const list = Array.isArray(next[listKey]) ? [...next[listKey]] : [];
          if (!list[index]) return;
          list[index] = { ...list[index], ...patch };
          next[listKey] = list;
        });

        return next;
      });
    };

    hydrateCompositeDescriptions();

    return () => {
      isCancelled = true;
    };
  }, [formData, getCompositeColumns, resolveDictionaryValue]);

  useEffect(
    () => () => {
      if (autoSaveStatusTimeoutRef.current) {
        clearTimeout(autoSaveStatusTimeoutRef.current);
      }

      if (typingAutoSaveTimeoutRef.current) {
        clearTimeout(typingAutoSaveTimeoutRef.current);
      }
    },
    []
  );

  const persistEco = useCallback(
    async ({ silent = false, revalidate = true } = {}) => {
      if (!id || !formData) {
        if (!silent) toast.warn('Erro de validação!');
        return false;
      }

      try {
        if (silent) {
          setAutosaving(true);
          setAutoSaveUiState('saving');
        } else {
          setSaving(true);
        }

        const res = await updateEcoById(id, formData);

        if (revalidate) {
          await mutate(['ecodoppler', id]);
        }

        if (!silent) {
          toast.success(res?.message || 'Laudo atualizado.');
        } else {
          if (autoSaveStatusTimeoutRef.current) {
            clearTimeout(autoSaveStatusTimeoutRef.current);
          }
          setAutoSaveUiState('saved');
          autoSaveStatusTimeoutRef.current = setTimeout(() => {
            setAutoSaveUiState('idle');
          }, 2500);
        }

        setHasUnsavedChanges(false);
        return true;
      } catch (err) {
        if (silent) {
          toast.error(err.response?.data?.message || 'Falha no auto-save ao trocar de aba.');
          if (autoSaveStatusTimeoutRef.current) {
            clearTimeout(autoSaveStatusTimeoutRef.current);
          }
          setAutoSaveUiState('error');
          autoSaveStatusTimeoutRef.current = setTimeout(() => {
            setAutoSaveUiState('idle');
          }, 4000);
        } else {
          toast.error(err.response?.data?.message || 'Erro ao gravar.');
        }
        return false;
      } finally {
        setSaving(false);
        setAutosaving(false);
      }
    },
    [id, formData]
  );

  const handleGravar = async () => {
    await persistEco();
  };

  const handleAnexar = () => toast.info('em desenvolvimento...');

  const handleVisualizar = () => {
    if (!id) {
      toast.warn('Erro de validação!');
      return;
    }
    history.push(`/visualizacao-ecocongenito/${id}`);
  };

  useEffect(() => {
    if (!id || !formData || !hasUnsavedChanges || saving || autosaving) {
      if (typingAutoSaveTimeoutRef.current) {
        clearTimeout(typingAutoSaveTimeoutRef.current);
      }
      return;
    }

    typingAutoSaveTimeoutRef.current = setTimeout(() => {
      persistEco({ silent: true, revalidate: false });
    }, 1200);

    return () => {
      if (typingAutoSaveTimeoutRef.current) {
        clearTimeout(typingAutoSaveTimeoutRef.current);
      }
    };
  }, [id, formData, hasUnsavedChanges, saving, autosaving, persistEco]);

  const setField = (field, value) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const setListField = (listKey, keyName, index, value) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [keyName]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const setListFields = (listKey, index, patch) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...patch };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey, keyName, codeKey = 'COD') => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      list.push({ [codeKey]: '', [keyName]: '' });
      return { ...prev, [listKey]: list };
    });
  };

  const addCompositeListItem = (listKey, keyName) => {
    const columns = getCompositeColumns(listKey, keyName);

    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      const newItem = {};

      columns.forEach((column) => {
        newItem[column.codeKey] = '';
        newItem[column.textKey] = '';
      });

      list.push(newItem);
      return { ...prev, [listKey]: list };
    });
  };

  const removeListItem = (listKey, index) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list.splice(index, 1);
      return { ...prev, [listKey]: list };
    });
  };

  const handleResolveListCode = async (listKey, keyName, targetCodeKey, index, codeValue) => {
    const listConfig = listDictionaryConfig[listKey] || {};
    const codeKey = listConfig.codeKey || 'COD';
    const extraFields = Array.isArray(listConfig.extraFields) ? listConfig.extraFields : [];
    const fieldConfigs = [
      {
        codeKey,
        group: listConfig.group || listKey,
        textKey: listConfig.textKey || keyName,
      },
      ...extraFields,
    ];
    const normalizedCode = String(codeValue || '').trim();
    const resolvingKey = `${listKey}:${index}`;
    const currentItem = formData?.[listKey]?.[index] || {};
    const draftItem = { ...currentItem, [targetCodeKey]: normalizedCode };

    if (!fieldConfigs.some((field) => String(draftItem[field.codeKey] || '').trim())) {
      const clearedPatch = fieldConfigs.reduce(
        (patch, field) => ({
          ...patch,
          [field.codeKey]: '',
          [field.textKey]: '',
        }),
        {}
      );
      setListFields(listKey, index, clearedPatch);
      return;
    }

    setResolvingRows((prev) => ({ ...prev, [resolvingKey]: true }));

    try {
      const nextPatch = {};

      for (const field of fieldConfigs) {
        const fieldCode = String(draftItem[field.codeKey] || '').trim();

        if (!fieldCode) {
          nextPatch[field.codeKey] = '';
          nextPatch[field.textKey] = '';
          continue;
        }

        const resolved = await resolveDictionaryValue({
          group: field.group,
          code: fieldCode,
        });
        nextPatch[field.codeKey] = resolved?.codigo || fieldCode;
        nextPatch[field.textKey] = resolved?.descricao || '';

        if (!resolved?.descricao && field.codeKey === targetCodeKey) {
          toast.warn(`Código ${fieldCode} não encontrado no dicionário.`);
        }
      }

      setListFields(listKey, index, nextPatch);
    } catch (error) {
      setListFields(listKey, index, { [targetCodeKey]: normalizedCode });
      toast.error(
        error?.response?.data?.message || 'Erro ao consultar o dicionário do ecocongenito.'
      );
    } finally {
      setResolvingRows((prev) => {
        const next = { ...prev };
        delete next[resolvingKey];
        return next;
      });
    }
  };

  const getListHandlers = (listKey, keyName = 'DESCRICAO', customCodeKey) => {
    const listConfig = listDictionaryConfig[listKey] || {};
    const codeKey = customCodeKey || listConfig.codeKey || 'COD';

    return {
      editable: true,
      keyName,
      codeKey,
      onChangeItem: (index, value) => setListField(listKey, keyName, index, value),
      onChangeCode: (index, value, targetCodeKey = codeKey) =>
        setListField(listKey, targetCodeKey, index, value),
      onResolveCode: (index, value, targetCodeKey = codeKey) =>
        handleResolveListCode(listKey, keyName, targetCodeKey, index, value),
      onAddItem: () => addListItem(listKey, keyName, codeKey),
      onRemoveItem: (index) => removeListItem(listKey, index),
      resolvingKeySet: new Set(
        Object.keys(resolvingRows)
          .filter((key) => key.startsWith(`${listKey}:`))
          .map((key) => key.split(':')[1])
      ),
    };
  };

  const getCompositeListHandlers = (listKey, keyName) => {
    const columns = getCompositeColumns(listKey, keyName);
    const primaryCodeKey = columns[0]?.codeKey || 'COD';

    return {
      editable: true,
      columns,
      onChangeCode: (index, value, targetCodeKey = primaryCodeKey) =>
        setListField(listKey, targetCodeKey, index, value),
      onResolveCode: (index, value, targetCodeKey = primaryCodeKey) =>
        handleResolveListCode(listKey, keyName, targetCodeKey, index, value),
      onAddItem: () => addCompositeListItem(listKey, keyName),
      onRemoveItem: (index) => removeListItem(listKey, index),
      resolvingKeySet: new Set(
        Object.keys(resolvingRows)
          .filter((key) => key.startsWith(`${listKey}:`))
          .map((key) => key.split(':')[1])
      ),
    };
  };

  const loadDictionaryTab = async (groupKey, { force = false } = {}) => {
    if (!groupKey) return;
    if (!force && Array.isArray(dictionaryData[groupKey])) return;

    setDictionaryLoading((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const data = await listEcoDictionaryItems({ grupo: groupKey });
      setDictionaryData((prev) => ({ ...prev, [groupKey]: data }));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao carregar dicionário.');
    } finally {
      setDictionaryLoading((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleOpenDictionaryModal = () => {
    setShowDictionaryModal(true);
    loadDictionaryTab(activeDictionaryTab);
  };

  const handleChangeDictionaryCell = (groupKey, index, field, value) => {
    setDictionaryData((prev) => {
      const list = Array.isArray(prev[groupKey]) ? [...prev[groupKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [groupKey]: list };
    });
  };

  const handleSaveDictionaryRow = async (groupKey, row) => {
    const codigo = String(row?.codigo || '').trim();
    const descricao = String(row?.descricao || '').trim();
    if (!codigo || !descricao) {
      toast.warn('Código e descrição são obrigatórios.');
      return;
    }

    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertEcoDictionaryItem({ grupo: groupKey, codigo, descricao });
      toast.success('Dicionário atualizado.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao salvar item do dicionário.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const handleSaveDictionaryDraft = async (groupKey) => {
    const draft = dictionaryDraft[groupKey] || { codigo: '', descricao: '' };
    const codigo = String(draft.codigo || '').trim();
    const descricao = String(draft.descricao || '').trim();

    if (!codigo || !descricao) {
      toast.warn('Informe código e descrição para incluir novo item.');
      return;
    }

    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertEcoDictionaryItem({ grupo: groupKey, codigo, descricao });
      setDictionaryDraft((prev) => ({
        ...prev,
        [groupKey]: { codigo: '', descricao: '' },
      }));
      toast.success('Novo item incluído no dicionário.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao incluir item no dicionário.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const handleTabSelect = async (nextTab) => {
    const targetTab = nextTab || activeTab;
    if (targetTab === activeTab) return;

    if (hasUnsavedChanges && !saving && !autosaving) {
      const saved = await persistEco({ silent: true, revalidate: false });
      if (!saved) return;
    }

    setActiveTab(targetTab);
  };

  const renderTabContent = (key) => {
    if (!formData) return null;

    switch (key) {
      case 'dados-pessoais':
        return (
          <>
            <div className="row g-3 legacy-panel-grid">
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Paciente</div>
                  <div className="legacy-field-value">
                    {formData.CODPAC} | {formData.NOMEPAC}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Convênio</div>
                  <div className="legacy-field-value">{formData.SIGLACONV || '-'}</div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Médico Solicitou</div>
                  <div className="legacy-field-value">{formData.Solicitou || '-'}</div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Médico Realizou</div>
                  <div className="legacy-field-value">{formData.Realizou || '-'}</div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Data do Exame</div>
                  <div className="legacy-field-value">{formData.dataEcoFmt || '-'}</div>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Tipo do Laudo</div>
                  <div className="legacy-radio-row d-flex align-items-center gap-3 flex-wrap">
                    <Form.Check inline type="radio" label="Normal" checked readOnly />
                    <Form.Check inline type="radio" label="Alterado" readOnly />
                  </div>
                </div>
              </div>
            </div>
            <ListaDicionario
              title="Cod Ecocardiograma"
              items={formData.obs}
              {...getListHandlers('obs')}
            />
          </>
        );

      case 'funcao-sistolica':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Septo"
                  unit="cm"
                  value={formData.SEPTO}
                  editable
                  onChange={(v) => setField('SEPTO', v)}
                />
                <CampoMedida
                  label="PP VE"
                  unit="cm"
                  value={formData.PPVE}
                  editable
                  onChange={(v) => setField('PPVE', v)}
                />
                <CampoMedida
                  label="Diâmetro Diastólico"
                  unit="cm"
                  value={formData.DD}
                  editable
                  onChange={(v) => setField('DD', v)}
                />
                <CampoMedida
                  label="Diâmetro Sistólico"
                  unit="cm"
                  value={formData.DS}
                  editable
                  onChange={(v) => setField('DS', v)}
                />
                <CampoMedida
                  label="Fração de Ejeção (Monoplanar)"
                  value={formData.fracaoEjecaoMono}
                  editable
                  onChange={(v) => setField('fracaoEjecaoMono', v)}
                />
                <CampoMedida
                  label="Fração de Ejeção (Biplanar)"
                  value={formData.FRAEJECBIPLA}
                  editable
                  onChange={(v) => setField('FRAEJECBIPLA', v)}
                />
                <CampoMedida
                  label="Massa do VE"
                  unit="g/m2"
                  value={formData.massaVE}
                  editable
                  onChange={(v) => setField('massaVE', v)}
                />
              </div>
            </div>

            <ListaDicionario
              title="Achados descritivos do VE"
              items={formData.achados}
              {...getListHandlers('achados')}
            />
            <ListaDicionarioComposta
              title="Contração Segmentar"
              items={formData.contraRegiao}
              {...getCompositeListHandlers('contraRegiao', 'CONTRASEG')}
            />
          </>
        );

      case 'ae-vm':
        return (
          <>
            <CampoMedida
              label="Diâmetro Átrio Esquerdo"
              unit="cm"
              value={formData.DAE}
              editable
              onChange={(v) => setField('DAE', v)}
            />
            <CampoMedida
              label="Volume do AE"
              unit="cc/m2"
              value={formData.VOL_AE}
              editable
              onChange={(v) => setField('VOL_AE', v)}
            />
            <ListaDicionario
              title="Átrio Esquerdo"
              items={formData.atrioEsq}
              {...getListHandlers('atrioEsq')}
            />
            <ListaDicionario
              title="Valva Mitral"
              items={formData.valvaMitral}
              {...getListHandlers('valvaMitral')}
            />
          </>
        );

      case 'valva-ao-aorta':
        return (
          <>
            <CampoMedida
              label="Diâmetro do Anel Valva Aórtica"
              unit="cm"
              value={formData.DAVAO}
              editable
              onChange={(v) => setField('DAVAO', v)}
            />
            <ListaDicionario
              title="Valva Aórtica"
              items={formData.valvaAO}
              {...getListHandlers('valvaAO')}
            />
            <CampoMedida
              label="Gradiente Médio Via de Saída do VE"
              unit="mmHg"
              value={formData.GMVSVE}
              editable
              onChange={(v) => setField('GMVSVE', v)}
            />
            <ListaDicionario title="Aorta" items={formData.aorta} {...getListHandlers('aorta')} />
          </>
        );

      case 'cavidades-direita-valvar':
        return (
          <>
            <ListaDicionario
              title="Átrio Direito"
              items={formData.atrioDir}
              {...getListHandlers('atrioDir')}
            />
            <ListaDicionario
              title="Veia Cava Inferior"
              items={formData.veiaCavaInf}
              {...getListHandlers('veiaCavaInf')}
            />
            <ListaDicionario
              title="Ventrículo Direito"
              items={formData.ventriDir}
              {...getListHandlers('ventriDir')}
            />
            <ListaDicionario
              title="Valva Tricúspide"
              items={formData.valvaTricu}
              {...getListHandlers('valvaTricu')}
            />
            <CampoMedida
              label="Diâmetro Anel Valva Pulmonar"
              unit="cm"
              value={formData.DAVP}
              editable
              onChange={(v) => setField('DAVP', v)}
            />
            <ListaDicionario
              title="Valva Pulmonar"
              items={formData.valvaPulmo}
              {...getListHandlers('valvaPulmo')}
            />
          </>
        );

      case 'pericardio':
        return (
          <ListaDicionario
            title="Pericárdio"
            items={formData.pericardio}
            {...getListHandlers('pericardio')}
          />
        );

      case 'obs-congenitas':
        return (
          <>
            <ListaDicionario
              title="Situs Cardíaco"
              items={formData.situsCardiaco}
              {...getListHandlers('situsCardiaco')}
            />
            <ListaDicionario
              title="Septo Interatrial"
              items={formData.septoInteratrial}
              {...getListHandlers('septoInteratrial')}
            />
            <ListaDicionarioComposta
              title="Septo Interventricular"
              items={formData.septoInterventricular}
              {...getCompositeListHandlers('septoInterventricular', 'DESCRICAO')}
            />
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Diâmetro VSVD"
                  unit="cm"
                  value={formData.VSVD}
                  editable
                  onChange={(v) => setField('VSVD', v)}
                />
                <CampoMedida
                  label="Gradiente VEVD"
                  unit="mmHg"
                  value={formData.gradienteVEVD}
                  editable
                  onChange={(v) => setField('gradienteVEVD', v)}
                />
                <CampoMedida
                  label="Relação P/S"
                  value={formData.relacaoPS}
                  editable
                  onChange={(v) => setField('relacaoPS', v)}
                />
                <CampoMedida
                  label="VTI Pulmonar"
                  unit="cm"
                  value={formData.VTIPulmonar}
                  editable
                  onChange={(v) => setField('VTIPulmonar', v)}
                />
                <CampoMedida
                  label="VTI VSVE"
                  unit="cm"
                  value={formData.VTIVSE}
                  editable
                  onChange={(v) => setField('VTIVSE', v)}
                />
                <CampoMedida
                  label="Fluxo Pulmonar"
                  value={formData.fluxoPulmonar}
                  editable
                  onChange={(v) => setField('fluxoPulmonar', v)}
                />
                <CampoMedida
                  label="Fluxo Sistêmico"
                  value={formData.fluxoSistemico}
                  editable
                  onChange={(v) => setField('fluxoSistemico', v)}
                />
                <CampoMedida
                  label="Estimativa PS VD"
                  unit="mmHg"
                  value={formData.estimativaPSVD}
                  editable
                  onChange={(v) => setField('estimativaPSVD', v)}
                />
              </div>
            </div>
            <ListaDicionario
              title="Concordância Atrioventricular"
              items={formData.concordanciaAtrioVent}
              {...getListHandlers('concordanciaAtrioVent')}
            />
          </>
        );

      case 'congenitos':
        return (
          <>
            <ListaDicionario
              title="Vias de Saída"
              items={formData.viasSaida}
              {...getListHandlers('viasSaida')}
            />
            <ListaDicionario
              title="Concordância Ventricular Arterial"
              items={formData.concordanciaVentArt}
              {...getListHandlers('concordanciaVentArt')}
            />
            <ListaDicionario
              title="Artéria Pulmonar"
              items={formData.arteriaPulmonar}
              {...getListHandlers('arteriaPulmonar')}
            />
            <ListaDicionario
              title="Artéria Coronária"
              items={formData.arteriaCoronaria}
              {...getListHandlers('arteriaCoronaria')}
            />
            {/*  <ListaDicionario
              title="Arco Aórtico"
              items={formData.arcoAortico}
              {...getListHandlers("arcoAortico")}
            />
            <ListaDicionario
              title="Conexão Veno Atrial"
              items={formData.conexaoVenoAtrial}
              {...getListHandlers("conexaoVenoAtrial")}
            />
            <ListaDicionario
              title="Estruturas Anômalas"
              items={formData.estruturasAnomas}
              {...getListHandlers("estruturasAnomas")}
            /> */}
          </>
        );

      case 'diagnosticos':
        return (
          <>
            {/*  <ListaDicionario
              title="Vias de Saída"
              items={formData.viasSaida}
              {...getListHandlers("viasSaida")}
            />
            <ListaDicionario
              title="Concordância Ventricular Arterial"
              items={formData.concordanciaVentArt}
              {...getListHandlers("concordanciaVentArt")}
            />
            <ListaDicionario
              title="Artéria Pulmonar"
              items={formData.arteriaPulmonar}
              {...getListHandlers("arteriaPulmonar")}
            />
            <ListaDicionario
              title="Artéria Coronária"
              items={formData.arteriaCoronaria}
              {...getListHandlers("arteriaCoronaria")}
            /> */}
            <ListaDicionario
              title="Arco Aórtico"
              items={formData.arcoAortico}
              {...getListHandlers('arcoAortico')}
            />
            <ListaDicionario
              title="Conexão Veno Atrial"
              items={formData.conexaoVenoAtrial}
              {...getListHandlers('conexaoVenoAtrial')}
            />
            <ListaDicionario
              title="Estruturas Anômalas"
              items={formData.estruturasAnomas}
              {...getListHandlers('estruturasAnomas')}
            />
          </>
        );

      case 'valv-mitral':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Área da Valva Mitral"
                  unit="cm2"
                  value={formData.AVM}
                  editable
                  onChange={(v) => setField('AVM', v)}
                />
                <CampoMedida
                  label="Gradiente Médio"
                  unit="mmHg"
                  value={formData.GMVM}
                  editable
                  onChange={(v) => setField('GMVM', v)}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="Diâmetro do Anel Mitral"
                  unit="cm"
                  value={formData.DAM}
                  editable
                  onChange={(v) => setField('DAM', v)}
                />
                <CampoMedida
                  label="VTI Mitral"
                  unit="cm"
                  value={formData.VTIMitral}
                  editable
                  onChange={(v) => setField('VTIMitral', v)}
                />
                <CampoMedida
                  label="VTI VSVE"
                  unit="cm"
                  value={formData.VTIVSE}
                  editable
                  onChange={(v) => setField('VTIVSE', v)}
                />
                <CampoMedida
                  label="Fração Regurgitação Mitral"
                  unit="%"
                  value={formData.FRM}
                  editable
                  onChange={(v) => setField('FRM', v)}
                />
              </div>
            </div>

            <ListaDicionario
              title="Refluxo da Valva Mitral"
              items={formData.refVM}
              {...getListHandlers('refVM')}
            />

            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Raio de Convergência IM"
                  unit="cm"
                  value={formData.raioConvergenciaIM}
                  editable
                  onChange={(v) => setField('raioConvergenciaIM', v)}
                />
                <CampoMedida
                  label="Velocidade de Aliasing"
                  unit="cm/seg"
                  value={formData.velocidadeAliasing}
                  editable
                  onChange={(v) => setField('velocidadeAliasing', v)}
                />
                <CampoMedida
                  label="Velocidade Máx R Mitral"
                  unit="cm/seg"
                  value={formData.velocidadeMaxRMitral}
                  editable
                  onChange={(v) => setField('velocidadeMaxRMitral', v)}
                />
                <CampoMedida
                  label="VTI Regurgitação Mitral"
                  unit="cm"
                  value={formData.VTIRegurgMitral}
                  editable
                  onChange={(v) => setField('VTIRegurgMitral', v)}
                />
                <CampoMedida
                  label="Área de Refluxo IM Color"
                  value={formData.areaRefluxoIMColor}
                  editable
                  onChange={(v) => setField('areaRefluxoIMColor', v)}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="Área de AE"
                  value={formData.areaAE}
                  editable
                  onChange={(v) => setField('areaAE', v)}
                />
                <CampoMedida
                  label="Relação ao Color IM/AE"
                  value={formData.RELIMAECLL}
                  editable
                  onChange={(v) => setField('RELIMAECLL', v)}
                />
                <CampoMedida
                  label="Fluxo Regurgitação Mitral"
                  unit="cm3/seg"
                  value={formData.fluxoRegurgMitral}
                  editable
                  onChange={(v) => setField('fluxoRegurgMitral', v)}
                />
                <CampoMedida
                  label="Orifício Regurgitação - PISA"
                  unit="cm2"
                  value={formData.orificioRM}
                  editable
                  onChange={(v) => setField('orificioRM', v)}
                />
                <CampoMedida
                  label="Volume Regurgitação Mitral"
                  unit="cc"
                  value={formData.VOLUME_VM}
                  editable
                  onChange={(v) => setField('VOLUME_VM', v)}
                />
              </div>
            </div>
          </>
        );

      case 'valv-aortica':
        return (
          <>
            <div className="legacy-split-grid">
              <div className="legacy-panel">
                <CampoMedida
                  label="Área Valva Aórtica"
                  unit="cm2"
                  value={formData.AREA_AO}
                  editable
                  onChange={(v) => setField('AREA_AO', v)}
                />
                <CampoMedida
                  label="Fração de Regurgitação Aórtica"
                  unit="%"
                  value={formData.FRA}
                  editable
                  onChange={(v) => setField('FRA', v)}
                />
                <CampoMedida
                  label="Gradiente Médio da Valva Aórtica"
                  unit="mmHg"
                  value={formData.GMVAO}
                  editable
                  onChange={(v) => setField('GMVAO', v)}
                />
                <CampoMedida
                  label="Gradiente Médio Via Saída do VE"
                  unit="mmHg"
                  value={formData.GMVSVE}
                  editable
                  onChange={(v) => setField('GMVSVE', v)}
                />
                <CampoMedida
                  label="VTI Via Saída do VE"
                  unit="cm"
                  value={formData.VTIVSVEAorta}
                  editable
                  onChange={(v) => setField('VTIVSVEAorta', v)}
                />
              </div>
              <div className="legacy-panel">
                <CampoMedida
                  label="VTI Aórtico"
                  unit="cm"
                  value={formData.VTIAortico}
                  editable
                  onChange={(v) => setField('VTIAortico', v)}
                />
                <CampoMedida
                  label="VTI Mitral"
                  unit="cm"
                  value={formData.VTIMitral}
                  editable
                  onChange={(v) => setField('VTIMitral', v)}
                />
                <CampoMedida
                  label="Diâmetro ao Color I Aórtica"
                  unit="cm"
                  value={formData.diametroColorIAortica}
                  editable
                  onChange={(v) => setField('diametroColorIAortica', v)}
                />
                <CampoMedida
                  label="Relação ao Color da I Aórtica"
                  value={formData.RELIAOCLL}
                  editable
                  onChange={(v) => setField('RELIAOCLL', v)}
                />
              </div>
            </div>

            <ListaDicionario
              title="Refluxo Valva Aórtica"
              items={formData.refVAO}
              {...getListHandlers('refVAO')}
            />
          </>
        );

      case 'valv-tri-pulm':
        return (
          <>
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ color: '#0066cc', marginBottom: '1rem' }}>Valva Tricúspide</h4>
              <CampoMedida
                label="Gradiente Médio V. Tricúspide"
                unit="mmHg"
                value={formData.GMVT}
                editable
                onChange={(v) => setField('GMVT', v)}
              />
              <CampoMedida
                label="Área de Refluxo Tricúspide Color"
                unit="cm2"
                value={formData.areaRefluxoTricuspideColor}
                editable
                onChange={(v) => setField('areaRefluxoTricuspideColor', v)}
              />
              <CampoMedida
                label="Velocidade de Pico Regurg Tricúspide"
                value={formData.velocidadePicoRegurgTricuspide}
                editable
                onChange={(v) => setField('velocidadePicoRegurgTricuspide', v)}
              />
              <CampoMedida
                label="Área do Átrio Direito"
                unit="cm2"
                value={formData.areaAtrioDireito}
                editable
                onChange={(v) => setField('areaAtrioDireito', v)}
              />
              <CampoMedida
                label="Gradiente de Pico da IT"
                unit="mmHg"
                value={formData.gradientePicoIT}
                editable
                onChange={(v) => setField('gradientePicoIT', v)}
              />
              <CampoMedida
                label="Estimativa Pressão Sistólica VD"
                unit="mmHg"
                value={formData.estimativaPSVD}
                editable
                onChange={(v) => setField('estimativaPSVD', v)}
              />
              <ListaDicionario
                title="Refluxo Válva Tricúspide"
                items={formData.refVT}
                {...getListHandlers('refVT')}
              />
            </div>

            <div>
              <h4 style={{ color: '#0066cc', marginBottom: '1rem' }}>Valva Pulmonar</h4>
              <CampoMedida
                label="Gradiente Médio V. Pulmonar"
                unit="mmHg"
                value={formData.GMVP}
                editable
                onChange={(v) => setField('GMVP', v)}
              />
              <ListaDicionario
                title="Refluxo Valva Pulmonar"
                items={formData.refVP}
                {...getListHandlers('refVP')}
              />
            </div>
          </>
        );

      case 'valv-proteses':
        return (
          <>
            <CampoMedida
              label="Gradiente Médio da Prótese Mitral"
              unit="mmHg"
              value={formData.GMPM}
              editable
              onChange={(v) => setField('GMPM', v)}
            />
            <ListaDicionarioComposta
              title="Valvulopatia em Próteses"
              items={formData.refluxoPM}
              {...getCompositeListHandlers('refluxoPM', 'refluxoPM')}
            />
            <CampoMedida
              label="Gradiente Médio da Prótese Aórtica"
              unit="mmHg"
              value={formData.GMPAO}
              editable
              onChange={(v) => setField('GMPAO', v)}
            />
            <ListaDicionarioComposta
              title="Prótese Aórtica"
              items={formData.refluxoPAO}
              {...getCompositeListHandlers('refluxoPAO', 'refluxoPAO')}
            />
            <CampoMedida
              label="Gradiente Médio da Prótese Tricúspide"
              unit="mmHg"
              value={formData.GMPT}
              editable
              onChange={(v) => setField('GMPT', v)}
            />
            <ListaDicionarioComposta
              title="Prótese Tricúspide"
              items={formData.refluxoPT}
              {...getCompositeListHandlers('refluxoPT', 'refluxoPT')}
            />
            <CampoMedida
              label="Gradiente Médio da Prótese Pulmonar"
              unit="mmHg"
              value={formData.GMPP}
              editable
              onChange={(v) => setField('GMPP', v)}
            />
            <ListaDicionarioComposta
              title="Prótese Pulmonar"
              items={formData.refluxoPP}
              {...getCompositeListHandlers('refluxoPP', 'refluxoPP')}
            />
          </>
        );

      case 'conclusoes':
        return (
          <>
            <ListaDicionarioComposta
              title="Valvulopatias"
              items={formData.valvulopatia}
              {...getCompositeListHandlers('valvulopatia', 'valvulopatia')}
            />
            <ListaDicionarioComposta
              title="Próteses"
              items={formData.protese}
              {...getCompositeListHandlers('protese', 'protese')}
            />
            <ListaDicionarioComposta
              title="Contração Segmentar"
              items={formData.contraRegiao}
              {...getCompositeListHandlers('contraRegiao', 'CONTRASEG')}
            />
          </>
        );

      case 'observacoes':
        return (
          <>
            <ListaDicionarioComposta
              title="Conclusões"
              items={formData.conclusoes}
              {...getCompositeListHandlers('conclusoes', 'conclusoes')}
            />
            <Form.Group>
              <strong>Obs:</strong>
              <Form.Control
                as="textarea"
                rows={6}
                className="legacy-observacoes-area"
                value={formData.OBSERVACOES ?? ''}
                onChange={(e) => setField('OBSERVACOES', e.target.value)}
              />
            </Form.Group>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="mb-3 mt-2 no-print legacy-eco-card">
      <Card.Header className="legacy-header">
        <div className="legacy-header-side legacy-header-side-left">
          <div className="legacy-header-eco-wrap">
            <div className="legacy-mini-label">Eco Nº</div>
            <div className="legacy-mini-value">{eco?.CODECO || '-'}</div>
          </div>
          <span className="legacy-cloud-save-slot" aria-hidden={autoSaveUiState === 'idle'}>
            <AutoSaveCloudIndicator
              status={autoSaveUiState}
              className="legacy-cloud-save-header"
              compact
            />
          </span>
        </div>
        <div className="legacy-header-title-wrap text-center">
          <div className="legacy-header-title">Congênito</div>
        </div>
        <div className="legacy-header-side legacy-header-side-right">
          <div className="legacy-header-date-wrap">
            <div className="legacy-mini-label">Data</div>
            <div className="legacy-mini-value">{eco?.dataEcoFmt || '-'}</div>
          </div>
          <Button
            variant="outline-primary"
            className="legacy-dictionaries-btn"
            onClick={handleOpenDictionaryModal}
          >
            Dicionários
          </Button>
        </div>
      </Card.Header>
      {isLoading && (
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      )}

      {error && !isLoading && (
        <Card.Body className="text-center text-danger py-5">
          {error.message || 'Erro ao carregar o laudo.'}
        </Card.Body>
      )}

      {eco && !isLoading && !error && (
        <>
          <Card.Body className="legacy-card-body">
            <div className="legacy-content-shell container-fluid px-0">
              <Tab.Container activeKey={activeTab} onSelect={(k) => handleTabSelect(k)}>
                <Nav variant="pills" className="legacy-tabs-row legacy-tabs-row-bottom flex-wrap">
                  {bottomTabList.map(({ key, label }) => (
                    <Nav.Item key={key}>
                      <Nav.Link eventKey={key} className="legacy-tab-link">
                        {label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <Nav variant="pills" className="legacy-tabs-row legacy-tabs-row-top flex-wrap">
                  {topTabList.map(({ key, label }) => (
                    <Nav.Item key={key}>
                      <Nav.Link eventKey={key} className="legacy-tab-link">
                        {label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="legacy-section-title-wrap">
                  <div className="legacy-section-title">
                    {tabHeadingMap[activeTab] || 'Ecocardiograma Congênito'}
                  </div>
                  <div className="legacy-section-subtitle">
                    Preenchimento estruturado com auto-save e listas vinculadas a dicionários.
                  </div>
                </div>

                <Tab.Content className="legacy-tab-content">
                  {tabList.map(({ key, label }) => (
                    <Tab.Pane key={key} eventKey={key} title={label}>
                      {renderTabContent(key) || (
                        <p className="text-muted mb-0">Nenhum dado para &quot;{label}&quot;.</p>
                      )}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Tab.Container>
            </div>
          </Card.Body>
          <Card.Footer className="legacy-footer no-print d-flex flex-wrap justify-content-center gap-2">
            <Button variant="success" onClick={handleGravar} disabled={saving || autosaving}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Gravar'}
            </Button>
            <Button variant="secondary" onClick={handleVisualizar}>
              Visualizar
            </Button>
            <Button variant="info" onClick={handleAnexar}>
              Anexar
            </Button>
          </Card.Footer>

          <Modal
            size="xl"
            show={showDictionaryModal}
            onHide={() => setShowDictionaryModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Dicionários do Ecocongenito</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Tab.Container
                activeKey={activeDictionaryTab}
                onSelect={(k) => {
                  const next = k || activeDictionaryTab;
                  setActiveDictionaryTab(next);
                  loadDictionaryTab(next);
                }}
              >
                <Nav variant="tabs" className="legacy-dictionary-tabs">
                  {dictionaryTabList.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <Tab.Content className="legacy-dictionary-content">
                  {dictionaryTabList.map((tab) => {
                    const list = Array.isArray(dictionaryData[tab.key])
                      ? dictionaryData[tab.key]
                      : [];
                    const draft = dictionaryDraft[tab.key] || {
                      codigo: '',
                      descricao: '',
                    };
                    const loading = dictionaryLoading[tab.key];

                    return (
                      <Tab.Pane key={tab.key} eventKey={tab.key}>
                        <div className="legacy-dictionary-new-row">
                          <Form.Control
                            type="text"
                            placeholder="Codigo"
                            value={draft.codigo}
                            onChange={(e) =>
                              setDictionaryDraft((prev) => ({
                                ...prev,
                                [tab.key]: {
                                  ...draft,
                                  codigo: e.target.value,
                                },
                              }))
                            }
                          />
                          <Form.Control
                            type="text"
                            placeholder="Descricao"
                            value={draft.descricao}
                            onChange={(e) =>
                              setDictionaryDraft((prev) => ({
                                ...prev,
                                [tab.key]: {
                                  ...draft,
                                  descricao: e.target.value,
                                },
                              }))
                            }
                          />
                          <Button
                            variant="success"
                            onClick={() => handleSaveDictionaryDraft(tab.key)}
                            disabled={savingDictionaryKey.startsWith(`${tab.key}:`)}
                          >
                            Incluir
                          </Button>
                        </div>

                        {loading ? (
                          <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <div className="legacy-dictionary-grid">
                            {list.map((row, index) => {
                              const rowKey = `${tab.key}:${row.codigo}`;
                              return (
                                <div
                                  className="legacy-dictionary-row"
                                  key={rowKey || `${tab.key}-${index}`}
                                >
                                  <Form.Control
                                    type="text"
                                    value={row.codigo}
                                    onChange={(e) =>
                                      handleChangeDictionaryCell(
                                        tab.key,
                                        index,
                                        'codigo',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <Form.Control
                                    type="text"
                                    value={row.descricao}
                                    onChange={(e) =>
                                      handleChangeDictionaryCell(
                                        tab.key,
                                        index,
                                        'descricao',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <Button
                                    variant="primary"
                                    onClick={() => handleSaveDictionaryRow(tab.key, row)}
                                    disabled={savingDictionaryKey === rowKey}
                                  >
                                    Salvar
                                  </Button>
                                </div>
                              );
                            })}

                            {list.length === 0 ? (
                              <p className="text-muted mb-0">
                                Nenhum item encontrado neste dicionario.
                              </p>
                            ) : null}
                          </div>
                        )}
                      </Tab.Pane>
                    );
                  })}
                </Tab.Content>
              </Tab.Container>
            </Modal.Body>
          </Modal>
        </>
      )}
    </Card>
  );
}
